import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { warpSvgWithFn } from "../../utils/displacementMap";
import {
    parseViewBoxParts,
    viewBoxAttrFromSvg,
} from "../../utils/channelSplitVector";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { isValidSvg } from "../../utils/svgUtils";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneLattice } from "../../utils/undoSnapshots";
import {
    makeBilinearWarp,
    makeIdentityLattice,
    resamplePathsInSvg,
    resizeLattice,
    type Lattice,
    type LatticePoint,
} from "../../utils/latticeWarp";
import {
    downscaleRasterDataUrlIfNeeded,
    isRasterImageFile,
    isSvgFile,
    loadImageNaturalSize,
    rasterDataUrlToLatticeSvg,
    readFileAsDataUrl,
    LATTICE_RASTER_MAX_EDGE,
} from "../../utils/latticeRasterSource";
import {
    buildRasterLatticeWarpSvg,
    type LatticeRasterSource,
} from "../../utils/latticeRasterMesh";

interface UploadedSvg {
    name: string;
    content: string;
}

export type DragScope = "single" | "row" | "column";

interface LatticeDeformState {
    svg: UploadedSvg | null;
    error: string | null;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleSvgLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;

    cols: number;
    rows: number;
    lattice: Lattice;
    resample: boolean;
    /** As a fraction of the viewBox diagonal (0.005 - 0.2). */
    maxSegmentFraction: number;
    circleSegments: number;

    showLattice: boolean;
    setShowLattice: (v: boolean) => void;

    /** When true, dragging a node is constrained to horizontal or vertical only. */
    lockAxis: boolean;
    setLockAxis: (v: boolean) => void;

    setCols: (v: number) => void;
    setRows: (v: number) => void;
    setResample: (v: boolean) => void;
    setMaxSegmentFraction: (v: number) => void;
    setCircleSegments: (v: number) => void;
    movePoint: (
        i: number,
        j: number,
        dx: number,
        dy: number,
        scope: DragScope,
    ) => void;
    /** Reset a single node back to its identity grid position. */
    resetNode: (i: number, j: number) => void;
    resetLattice: () => void;

    viewBoxAttr: string;
    vb: { x: number; y: number; width: number; height: number };
    warpedSvgContent: string | null;
    exportSvgString: string | null;
    canExport: boolean;


    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    pushLatticeSnapshot: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const LatticeDeformContext = createContext<LatticeDeformState | null>(null);

const DEFAULT_COLS = 5;
const DEFAULT_ROWS = 5;
const DEFAULT_RESAMPLE = true;
const DEFAULT_MAX_SEG_FRAC = 0.02;
const DEFAULT_CIRCLE_SEGMENTS = 48;
const DEFAULT_SHOW_LATTICE = true;
const DEFAULT_LOCK_AXIS = false;

const DEFAULT_LATTICE_DEFORM_PRESET: Record<string, unknown> = {
    cols: DEFAULT_COLS,
    rows: DEFAULT_ROWS,
    resample: DEFAULT_RESAMPLE,
    maxSegmentFraction: DEFAULT_MAX_SEG_FRAC,
    circleSegments: DEFAULT_CIRCLE_SEGMENTS,
    showLattice: DEFAULT_SHOW_LATTICE,
    lockAxis: DEFAULT_LOCK_AXIS,
    lattice: null,
};

export function useLatticeDeform() {
    const ctx = useContext(LatticeDeformContext);
    if (!ctx) throw new Error("useLatticeDeform must be used within LatticeDeformProvider");
    return ctx;
}

function lattice2dToFlat(lat: Lattice): number[] | null {
    if (!lat || lat.length === 0) return null;
    const flat: number[] = [];
    for (const row of lat) {
        for (const p of row) {
            flat.push(p.x, p.y);
        }
    }
    return flat;
}

function flatToLattice2d(
    flat: unknown,
    cols: number,
    rows: number,
): Lattice | null {
    if (!Array.isArray(flat)) return null;
    const expected = (cols + 1) * (rows + 1) * 2;
    if (flat.length !== expected) return null;
    const out: Lattice = [];
    let k = 0;
    for (let j = 0; j <= rows; j++) {
        const row: LatticePoint[] = [];
        for (let i = 0; i <= cols; i++) {
            const x = Number(flat[k++]);
            const y = Number(flat[k++]);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            row.push({ x, y });
        }
        out.push(row);
    }
    return out;
}

export function LatticeDeformProvider({ children }: { children: React.ReactNode }) {
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [rasterSource, setRasterSource] = useState<LatticeRasterSource | null>(null);
    const [error, setError] = useState<string | null>(null);
    const svgInputRef = useRef<HTMLInputElement | null>(null);

    const [cols, setColsState] = useState(DEFAULT_COLS);
    const [rows, setRowsState] = useState(DEFAULT_ROWS);
    const [resample, setResample] = useState(DEFAULT_RESAMPLE);
    const [maxSegmentFraction, setMaxSegmentFraction] = useState(DEFAULT_MAX_SEG_FRAC);
    const [circleSegments, setCircleSegments] = useState(DEFAULT_CIRCLE_SEGMENTS);
    const [showLattice, setShowLattice] = useState(DEFAULT_SHOW_LATTICE);
    const [lockAxis, setLockAxis] = useState(DEFAULT_LOCK_AXIS);

    const viewBoxAttr = useMemo(
        () => (svg ? viewBoxAttrFromSvg(svg.content) : "0 0 100 100"),
        [svg],
    );
    const vb = useMemo(() => {
        const p = parseViewBoxParts(viewBoxAttr);
        return p ?? { x: 0, y: 0, width: 100, height: 100 };
    }, [viewBoxAttr]);

    // Lattice state is keyed by current cols/rows; it is rebuilt to identity
    // whenever a new SVG is loaded (so the mesh fits the new viewBox) and is
    // resampled when cols/rows change so the user's edits survive.
    const [lattice, setLattice] = useState<Lattice>(() =>
        makeIdentityLattice(DEFAULT_COLS, DEFAULT_ROWS, {
            x: 0, y: 0, width: 100, height: 100,
        }),
    );

    const latticeRef = useRef(lattice);
    latticeRef.current = lattice;
    const history = useUndoRedo<Lattice>({ clone: cloneLattice });

    const pushLatticeSnapshot = useCallback(() => {
        history.push(latticeRef.current);
    }, [history]);

    const undo = useCallback(() => {
        history.undo(latticeRef.current, setLattice);
    }, [history]);

    const redo = useCallback(() => {
        history.redo(latticeRef.current, setLattice);
    }, [history]);

    const handleSvgLoad = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            const input = e.target;
            if (!file) return;
            setError(null);

            const applySvgText = (
                text: string,
                name: string,
                raster: LatticeRasterSource | null,
            ) => {
                if (!text) {
                    setError("Unable to read file.");
                    return;
                }
                if (!isValidSvg(text)) {
                    setError("File does not appear to be valid SVG.");
                    return;
                }
                const vbAttr = viewBoxAttrFromSvg(text);
                const parsed =
                    parseViewBoxParts(vbAttr) ?? { x: 0, y: 0, width: 100, height: 100 };
                setSvg({ name, content: text });
                setRasterSource(raster);
                setLattice(makeIdentityLattice(cols, rows, parsed));
                history.clear();
            };

            try {
                if (isSvgFile(file)) {
                    const text = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () =>
                            resolve(typeof reader.result === "string" ? reader.result : "");
                        reader.onerror = () => reject(new Error("Error reading file."));
                        reader.readAsText(file);
                    });
                    applySvgText(text, file.name, null);
                } else if (isRasterImageFile(file)) {
                    let dataUrl = await readFileAsDataUrl(file);
                    let { w, h } = await loadImageNaturalSize(dataUrl);
                    const scaled = await downscaleRasterDataUrlIfNeeded(
                        dataUrl,
                        w,
                        h,
                        LATTICE_RASTER_MAX_EDGE,
                    );
                    dataUrl = scaled.dataUrl;
                    w = scaled.w;
                    h = scaled.h;
                    const synthetic = rasterDataUrlToLatticeSvg(dataUrl, w, h);
                    applySvgText(synthetic, file.name, { dataUrl, w, h });
                } else {
                    setError("Please choose an SVG or a raster image (PNG, JPEG, WebP, ...).");
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Could not load file.");
            } finally {
                input.value = "";
            }
        },
        [cols, rows, history],
    );

    const setCols = useCallback((next: number) => {
        const target = Math.max(1, Math.min(64, Math.round(next)));
        setColsState((prevCols) => {
            if (prevCols === target) return prevCols;
            pushLatticeSnapshot();
            setLattice((prev) => resizeLattice(prev, prevCols, rows, target, rows));
            return target;
        });
    }, [rows, pushLatticeSnapshot]);

    const setRows = useCallback((next: number) => {
        const target = Math.max(1, Math.min(64, Math.round(next)));
        setRowsState((prevRows) => {
            if (prevRows === target) return prevRows;
            pushLatticeSnapshot();
            setLattice((prev) => resizeLattice(prev, cols, prevRows, cols, target));
            return target;
        });
    }, [cols, pushLatticeSnapshot]);

    const movePoint = useCallback(
        (i: number, j: number, dx: number, dy: number, scope: DragScope) => {
            if (dx === 0 && dy === 0) return;
            setLattice((prev) => {
                if (!prev[j] || !prev[j][i]) return prev;
                // Clone only the rows we touch - keeps the array reference work
                // proportional to the number of affected nodes.
                if (scope === "row") {
                    const newRow = prev[j].map((p) => ({ x: p.x + dx, y: p.y + dy }));
                    const out = prev.slice();
                    out[j] = newRow;
                    return out;
                }
                if (scope === "column") {
                    const out = prev.map((row) => {
                        if (!row[i]) return row;
                        const r = row.slice();
                        r[i] = { x: row[i].x + dx, y: row[i].y + dy };
                        return r;
                    });
                    return out;
                }
                const r = prev[j].slice();
                r[i] = { x: prev[j][i].x + dx, y: prev[j][i].y + dy };
                const out = prev.slice();
                out[j] = r;
                return out;
            });
        },
        [],
    );

    const resetNode = useCallback((i: number, j: number) => {
        pushLatticeSnapshot();
        setLattice((prev) => {
            if (!prev[j] || !prev[j][i]) return prev;
            const id = {
                x: vb.x + (i / cols) * vb.width,
                y: vb.y + (j / rows) * vb.height,
            };
            const r = prev[j].slice();
            r[i] = id;
            const out = prev.slice();
            out[j] = r;
            return out;
        });
    }, [cols, rows, vb.x, vb.y, vb.width, vb.height, pushLatticeSnapshot]);

    const resetLattice = useCallback(() => {
        pushLatticeSnapshot();
        setLattice(makeIdentityLattice(cols, rows, vb));
    }, [cols, rows, vb, pushLatticeSnapshot]);

    const warpedSvgContent = useMemo(() => {
        if (!svg) return null;
        if (rasterSource) {
            return buildRasterLatticeWarpSvg(rasterSource, lattice, vb);
        }
        const diagonal = Math.sqrt(vb.width * vb.width + vb.height * vb.height);
        const maxSegLen = Math.max(0.0001, diagonal * maxSegmentFraction);
        const source = resample ? resamplePathsInSvg(svg.content, maxSegLen) : svg.content;
        const displace = makeBilinearWarp(lattice, vb, cols, rows);
        return warpSvgWithFn(source, displace, circleSegments);
    }, [svg, rasterSource, lattice, cols, rows, vb, resample, maxSegmentFraction, circleSegments]);

    const exportSvgString = warpedSvgContent;
    const canExport = !!exportSvgString;

    const handleDownload = useCallback(() => {
        if (!exportSvgString || !svg) return;
        const blob = new Blob([exportSvgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const base = svg.name.replace(/\.svg$/i, "");
        link.download = `${base}-lattice-deform.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [exportSvgString, svg]);


    const getPresetValues = useCallback((): Record<string, unknown> => ({
        cols,
        rows,
        resample,
        maxSegmentFraction,
        circleSegments,
        showLattice,
        lockAxis,
        lattice: lattice2dToFlat(lattice),
    }), [cols, rows, resample, maxSegmentFraction, circleSegments, showLattice, lockAxis, lattice]);

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        const nextCols = typeof values.cols === "number" ? Math.max(1, Math.min(64, Math.round(values.cols))) : cols;
        const nextRows = typeof values.rows === "number" ? Math.max(1, Math.min(64, Math.round(values.rows))) : rows;
        if (typeof values.resample === "boolean") setResample(values.resample);
        if (typeof values.maxSegmentFraction === "number" && Number.isFinite(values.maxSegmentFraction)) {
            setMaxSegmentFraction(Math.max(0.001, Math.min(0.5, values.maxSegmentFraction)));
        }
        if (typeof values.circleSegments === "number" && Number.isFinite(values.circleSegments)) {
            setCircleSegments(Math.max(6, Math.min(256, Math.round(values.circleSegments))));
        }
        if (typeof values.showLattice === "boolean") setShowLattice(values.showLattice);
        if (typeof values.lockAxis === "boolean") setLockAxis(values.lockAxis);
        setColsState(nextCols);
        setRowsState(nextRows);

        const restored = flatToLattice2d(values.lattice, nextCols, nextRows);
        if (restored) {
            setLattice(restored);
        } else {
            // No (or invalid) lattice in preset -> rebuild identity to match new dims.
            setLattice(makeIdentityLattice(nextCols, nextRows, vb));
        }
    }, [cols, rows, vb]);

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "lattice-deform",
        defaultPreset: DEFAULT_LATTICE_DEFORM_PRESET,
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
    }, [history, resetStoredParameters]);

    useSvgToolHandoffReceiver(
        "lattice-deform",
        {
            onPrimary: (incoming) => {
                setError(null);
                setSvg(incoming);
                const vbAttr = viewBoxAttrFromSvg(incoming.content);
                const parsed = parseViewBoxParts(vbAttr) ?? { x: 0, y: 0, width: 100, height: 100 };
                setLattice(makeIdentityLattice(cols, rows, parsed));
                history.clear();
            },
        },
        { setError },
    );

    const value: LatticeDeformState = {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        cols,
        rows,
        lattice,
        resample,
        maxSegmentFraction,
        circleSegments,
        showLattice,
        setShowLattice,
        lockAxis,
        setLockAxis,
        setCols,
        setRows,
        setResample,
        setMaxSegmentFraction,
        setCircleSegments,
        movePoint,
        resetNode,
        resetLattice,
        viewBoxAttr,
        vb,
        warpedSvgContent,
        exportSvgString,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        pushLatticeSnapshot,
        undo,
        redo,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
    };

    return (
        <LatticeDeformContext.Provider value={value}>{children}</LatticeDeformContext.Provider>
    );
}
